import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concatMap,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';

import * as UserGroupActions from '../actions/employee-group.actions';
import { NotificationService, UsersService } from '@fc-core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { getEmployeeGroups } from '../selectors';
import { getErrorMessage } from '@fc-core/services/notification.utils';

@Injectable()
export class UserGroupEffects {
  loadUserGroups$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserGroupActions.loadEmployeeGroups),
      concatMap(() =>
        this.usersService.loadUserCollections().pipe(
          map((userGroups) =>
            UserGroupActions.loadEmployeeGroupsSuccess({ userGroups }),
          ),
          catchError((error) =>
            of(UserGroupActions.employeeGroupsFailure({ error })),
          ),
        ),
      ),
    );
  });

  updateUserGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserGroupActions.updateUserGroup),
      concatMap(({ userGroup }) =>
        this.usersService.updateUserCollection(userGroup).pipe(
          map((group) => {
            this.notifications.showSuccess('Group successfully updated');
            this.dialog.closeAll();
            return UserGroupActions.updateUserGroupSuccess({
              userGroup: group,
            });
          }),
          catchError((error) =>
            of(UserGroupActions.employeeGroupsFailure({ error })),
          ),
        ),
      ),
    );
  });

  createUserGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserGroupActions.createUserGroup),
      concatMap(({ userGroup }) =>
        this.usersService.createUserCollection(userGroup).pipe(
          map((group) => {
            this.notifications.showSuccess('Group successfully created');
            this.dialog.closeAll();
            this.router.navigate([`/users/main/user-groups/${group.id}/users`]);
            return UserGroupActions.createUserGroupSuccess({
              userGroup: group,
            });
          }),
          catchError((error) =>
            of(UserGroupActions.employeeGroupsFailure({ error })),
          ),
        ),
      ),
    );
  });

  deleteUserGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserGroupActions.deleteUserGroup),
      concatMap(({ groupId }) =>
        this.usersService.deleteUserCollection(groupId).pipe(
          withLatestFrom(this.store.select(getEmployeeGroups)),
          map(([_, groups]) => {
            this.notifications.showSuccess('Group successfully deleted');
            this.dialog.closeAll();
            const remainingGroups = groups.filter(
              (group) => group.id !== groupId,
            );
            if (remainingGroups.length !== 0) {
              this.router.navigate([
                `/users/main/user-groups/${remainingGroups[0].id}/users`,
              ]);
            } else {
              this.router.navigate(['/users/main/user-groups']);
            }
            return UserGroupActions.deleteUserGroupSuccess({ groupId });
          }),
          catchError((error) =>
            of(UserGroupActions.employeeGroupsFailure({ error })),
          ),
        ),
      ),
    );
  });

  addEmployeesToGroup = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserGroupActions.addEmployeesToGroup),
      concatMap(({ groupId, employeeList }) =>
        this.usersService.addEmployeeToGroup(groupId, employeeList).pipe(
          map((group) => {
            this.notifications.showSuccess('Employee successfully added');
            this.dialog.closeAll();
            return UserGroupActions.addEmployeesToGroupSuccess({
              group,
            });
          }),
          catchError((error) =>
            of(UserGroupActions.employeeGroupsFailure({ error })),
          ),
        ),
      ),
    );
  });

  removeEmployeesFromGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserGroupActions.removeEmployeesFromGroup),
      concatMap(({ groupId, employeeList }) =>
        this.usersService.removeEmployeeFromGroup(groupId, employeeList).pipe(
          map((userGroup) => {
            this.notifications.showSuccess('Employee successfully removed');
            this.dialog.closeAll();
            return UserGroupActions.removeEmployeesFromGroupSuccess({
              userGroup,
            });
          }),
          catchError((error) =>
            of(UserGroupActions.employeeGroupsFailure({ error })),
          ),
        ),
      ),
    );
  });

  userGroupsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserGroupActions.employeeGroupsFailure),
        switchMap(({ error }) => {
          this.notifications.showError(getErrorMessage(error));
          return EMPTY;
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private usersService: UsersService,
    private dialog: MatDialog,
    private router: Router,
    private store: Store,
    private notifications: NotificationService,
  ) {}
}
